package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.layout.engine.LayoutMode
import kotlin.jvm.JvmStatic
import kotlin.math.ceil

/**
 * 虚拟滚动列表：仅实例化视窗 + buffer 范围内的子节点，支持等高（或等宽）列表场景。
 *
 * 继承 [ScrollableContainer] 复用滚动状态、钳制、滚轮/拖拽事件接管。与全量 [ScrollView] 的区别：
 * - 内容尺寸由 `itemCount * itemHeight` 计算（覆盖 [contentHeight] / [contentWidth]），不遍历子节点，
 *   故 [requiresContentMeasure] 返回 `false`；
 * - [children] 仅返回当前窗口内已实例化的子元素；
 * - 窗口子节点 0 基排布（首个子对应 [firstVisible]），故覆盖 [contentOffsetY] / [contentOffsetX]
 *   叠加「窗口首项内容坐标」，让渲染/命中正确对齐；
 * - 外部通过 [materializeWindow] 注册回调，滚动或 itemCount 变化时由回调负责 diff 子节点。
 *
 * [direction] 默认 [ScrollDirection.VERTICAL]（向后兼容）；设为 [ScrollDirection.HORIZONTAL] 时
 * `itemHeight` 解释为「沿滚动轴的项尺寸」，列表横向排布并水平滚动。
 */
@Suppress("TooManyFunctions", "MagicNumber")
class VirtualList private constructor() : ScrollableContainer<VirtualList>() {

    private var itemHeightField: Float = 20f
    private var itemCountField: Int = 0
    private var bufferField: Int = 2

    /** 当前窗口内已实例化的子元素（由 materializer 回调填充）。 */
    private var windowChildren: List<UIElement<*>> = emptyList()

    /** 上一次 materialize 的窗口范围，用于判断是否需要重建。 */
    private var lastFirst = -1
    private var lastLast = -1
    private var lastItemCount = -1

    /** 窗口变化时的回调（由 IrInterpreter 注册）。 */
    private var materializer: WindowMaterializer? = null

    init {
        styleField.layout().mode = LayoutMode.COLUMN
    }

    override fun self(): VirtualList = this

    override fun direction(d: ScrollDirection): VirtualList {
        super.direction(d)
        // 横向列表按行排布。
        styleField.layout().mode = if (direction() == ScrollDirection.HORIZONTAL) LayoutMode.ROW else LayoutMode.COLUMN
        invalidateWindow()
        return this
    }

    // -------- 公开属性 --------

    fun itemHeight(h: Float): VirtualList = apply {
        itemHeightField = h.coerceAtLeast(1f)
        invalidateWindow()
    }

    fun itemHeight(): Float = itemHeightField

    fun itemCount(count: Int): VirtualList = apply {
        itemCountField = count.coerceAtLeast(0)
        // contentHeight/Width 已随 itemCount 变化，触发钳制后再算窗口。
        scrollY(scrollY())
        scrollX(scrollX())
        invalidateWindow()
    }

    fun itemCount(): Int = itemCountField

    fun buffer(b: Int): VirtualList = apply {
        bufferField = b.coerceAtLeast(0)
        invalidateWindow()
    }

    fun buffer(): Int = bufferField

    // -------- 内容尺寸（按数据量计算，不遍历子节点） --------

    override fun requiresContentMeasure(): Boolean = false

    override fun contentHeight(): Float =
        if (direction() == ScrollDirection.HORIZONTAL) viewportHeight() else itemCountField * itemHeightField

    override fun contentWidth(): Float =
        if (direction() == ScrollDirection.HORIZONTAL) itemCountField * itemHeightField else viewportWidth()

    // -------- 子内容偏移：窗口首项内容坐标 - 滚动偏移 --------

    override fun contentOffsetY(): Float =
        if (direction() == ScrollDirection.HORIZONTAL) 0f else firstVisible() * itemHeightField - scrollY()

    override fun contentOffsetX(): Float =
        if (direction() == ScrollDirection.HORIZONTAL) firstVisible() * itemHeightField - scrollX() else 0f

    // -------- 窗口计算 --------

    /** 沿滚动轴的当前偏移。 */
    private fun axisScroll(): Float = if (direction() == ScrollDirection.HORIZONTAL) scrollX() else scrollY()

    /** 沿滚动轴的视窗尺寸。 */
    private fun axisViewport(): Float =
        if (direction() == ScrollDirection.HORIZONTAL) viewportWidth() else viewportHeight()

    /** 当前视窗内第一个可见项的 index（含 buffer）。 */
    fun firstVisible(): Int = ((axisScroll() / itemHeightField).toInt() - bufferField).coerceAtLeast(0)

    /** 当前视窗内最后一个可见项的 index（含 buffer，不超过 itemCount-1）。 */
    fun lastVisible(): Int {
        val visibleCount = ceil(axisViewport() / itemHeightField).toInt()
        val raw = (axisScroll() / itemHeightField).toInt() + visibleCount + bufferField
        return raw.coerceAtMost(itemCountField - 1)
    }

    // -------- 子节点管理 --------

    override fun children(): List<UIElement<*>> = windowChildren

    /** 由 materializer 回调设置当前窗口子节点。 */
    fun setWindowChildren(children: List<UIElement<*>>?) {
        windowChildren = children ?: emptyList()
        // FR-30 B1：windowChildren 经 children() 返回、绕过会标脏的 child()/replaceChildren()，
        // 滚动实例化新窗口时须显式标布局脏，否则整树布局 memo 命中返回过期布局、新窗口项不被定位（滚动后内容消失）。
        markLayoutDirty()
    }

    /** 注册窗口变化回调。 */
    fun materializeWindow(m: WindowMaterializer) {
        materializer = m
    }

    /** 滚动状态变化时由基类回调，触发窗口重算。 */
    override fun onScrollStateChanged() {
        super.onScrollStateChanged()
        invalidateWindow()
    }

    /** 检查窗口是否变化，若变化则触发 materializer。 */
    private fun invalidateWindow() {
        val m = materializer ?: return
        if (itemCountField <= 0) {
            if (windowChildren.isNotEmpty()) {
                lastFirst = -1
                lastLast = -1
                lastItemCount = 0
                m.onWindowChanged(this, 0, -1)
            }
            return
        }
        val first = firstVisible()
        val last = lastVisible()
        if (first == lastFirst && last == lastLast && itemCountField == lastItemCount) {
            return
        }
        lastFirst = first
        lastLast = last
        lastItemCount = itemCountField
        m.onWindowChanged(this, first, last)
    }

    // -------- 回调接口 --------

    fun interface WindowMaterializer {
        /**
         * 窗口范围变化时调用。
         *
         * @param list  VirtualList 实例
         * @param first 第一个需要实例化的 index（含）
         * @param last  最后一个需要实例化的 index（含）；若 itemCount=0 则 last < first
         */
        fun onWindowChanged(list: VirtualList, first: Int, last: Int)
    }

    companion object {
        @JvmStatic
        fun create(): VirtualList = VirtualList()
    }
}
